import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BlacklistMonitor = () => {
  const [selectedIP, setSelectedIP] = useState(null);
  const [servers] = useState([
    {
      id: 'srv-1',
      ip: '203.0.113.45',
      name: 'Production Server 01',
      blacklists: [
        { name: 'Spamhaus', status: 'clear', url: 'https://www.spamhaus.org/lookup/' },
        { name: 'Barracuda', status: 'listed', url: 'https://www.barracudacentral.org/rbl/removal-request', reason: 'High complaint rate detected', listedDate: '2026-02-23' },
        { name: 'SURBL', status: 'clear', url: 'https://www.surbl.org/' },
        { name: 'SpamCop', status: 'clear', url: 'https://www.spamcop.net/bl.shtml' },
        { name: 'URIBL', status: 'clear', url: 'https://uribl.com/' }
      ]
    },
    {
      id: 'srv-2',
      ip: '203.0.113.46',
      name: 'Production Server 02',
      blacklists: [
        { name: 'Spamhaus', status: 'clear', url: 'https://www.spamhaus.org/lookup/' },
        { name: 'Barracuda', status: 'clear', url: 'https://www.barracudacentral.org/rbl/removal-request' },
        { name: 'SURBL', status: 'clear', url: 'https://www.surbl.org/' },
        { name: 'SpamCop', status: 'clear', url: 'https://www.spamcop.net/bl.shtml' },
        { name: 'URIBL', status: 'clear', url: 'https://uribl.com/' }
      ]
    },
    {
      id: 'srv-3',
      ip: '203.0.113.47',
      name: 'Canary Server 01',
      blacklists: [
        { name: 'Spamhaus', status: 'listed', url: 'https://www.spamhaus.org/lookup/', reason: 'Detected spam-like behavior', listedDate: '2026-02-22' },
        { name: 'Barracuda', status: 'listed', url: 'https://www.barracudacentral.org/rbl/removal-request', reason: 'Policy violation', listedDate: '2026-02-23' },
        { name: 'SURBL', status: 'clear', url: 'https://www.surbl.org/' },
        { name: 'SpamCop', status: 'listed', url: 'https://www.spamcop.net/bl.shtml', reason: 'Spam reports received', listedDate: '2026-02-24' },
        { name: 'URIBL', status: 'clear', url: 'https://uribl.com/' }
      ]
    },
    {
      id: 'srv-4',
      ip: '203.0.113.48',
      name: 'Production Server 03',
      blacklists: [
        { name: 'Spamhaus', status: 'clear', url: 'https://www.spamhaus.org/lookup/' },
        { name: 'Barracuda', status: 'clear', url: 'https://www.barracudacentral.org/rbl/removal-request' },
        { name: 'SURBL', status: 'clear', url: 'https://www.surbl.org/' },
        { name: 'SpamCop', status: 'clear', url: 'https://www.spamcop.net/bl.shtml' },
        { name: 'URIBL', status: 'clear', url: 'https://uribl.com/' }
      ]
    }
  ]);

  const getServerStatus = (server) => {
    const listedCount = server?.blacklists?.filter(bl => bl?.status === 'listed')?.length;
    if (listedCount === 0) return { status: 'clear', color: 'bg-success', textColor: 'text-success', label: 'Clear' };
    if (listedCount <= 2) return { status: 'warning', color: 'bg-warning', textColor: 'text-warning', label: 'Warning' };
    return { status: 'critical', color: 'bg-error', textColor: 'text-error', label: 'Critical' };
  };

  const handleViewDetails = (server) => {
    setSelectedIP(server);
  };

  const handleCloseModal = () => {
    setSelectedIP(null);
  };

  return (
    <div className="space-y-6">
      <div className="bg-muted rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-heading font-semibold text-foreground mb-1">
              Real-Time Blacklist Monitoring
            </h3>
            <p className="text-sm text-muted-foreground">
              Monitor your server IPs across major RBL databases
            </p>
          </div>
          <Button iconName="RefreshCw" variant="outline">
            Refresh Status
          </Button>
        </div>
      </div>

      {/* Server Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {servers?.map((server) => {
          const status = getServerStatus(server);
          const listedCount = server?.blacklists?.filter(bl => bl?.status === 'listed')?.length;
          
          return (
            <div key={server?.id} className="bg-card rounded-lg border border-border p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <div className={`w-3 h-3 rounded-full ${status?.color} animate-pulse-subtle`} />
                    <span className="text-sm font-medium text-foreground">{server?.name}</span>
                  </div>
                  <div className="text-xs text-muted-foreground font-mono">{server?.ip}</div>
                </div>
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                  status?.status === 'clear' ? 'bg-success/10 text-success' :
                  status?.status === 'warning'? 'bg-warning/10 text-warning' : 'bg-error/10 text-error'
                }`}>
                  {status?.label}
                </span>
              </div>

              <div className="space-y-2 mb-4">
                {server?.blacklists?.map((bl) => (
                  <div key={bl?.name} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{bl?.name}</span>
                    {bl?.status === 'clear' ? (
                      <Icon name="CheckCircle" size={16} className="text-success" />
                    ) : (
                      <Icon name="AlertCircle" size={16} className="text-error" />
                    )}
                  </div>
                ))}
              </div>

              {listedCount > 0 && (
                <div className="bg-error/10 rounded-lg p-3 mb-4">
                  <div className="flex items-center gap-2 text-error text-sm">
                    <Icon name="AlertTriangle" size={16} />
                    <span className="font-medium">{listedCount} Blacklist{listedCount > 1 ? 's' : ''} Detected</span>
                  </div>
                </div>
              )}

              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => handleViewDetails(server)}
              >
                View Details
              </Button>
            </div>
          );
        })}
      </div>

      {/* Detail Modal */}
      {selectedIP && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg border border-border max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-1">
                  {selectedIP?.name}
                </h3>
                <p className="text-sm text-muted-foreground font-mono">{selectedIP?.ip}</p>
              </div>
              <Button variant="ghost" size="icon" onClick={handleCloseModal}>
                <Icon name="X" size={20} />
              </Button>
            </div>

            <div className="p-6 space-y-4">
              {selectedIP?.blacklists?.map((bl) => (
                <div key={bl?.name} className={`rounded-lg border p-4 ${
                  bl?.status === 'listed' ? 'border-error bg-error/5' : 'border-border bg-muted'
                }`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      {bl?.status === 'clear' ? (
                        <Icon name="CheckCircle" size={24} className="text-success" />
                      ) : (
                        <Icon name="AlertCircle" size={24} className="text-error" />
                      )}
                      <div>
                        <div className="font-medium text-foreground">{bl?.name}</div>
                        <div className={`text-xs font-medium uppercase ${
                          bl?.status === 'clear' ? 'text-success' : 'text-error'
                        }`}>
                          {bl?.status}
                        </div>
                      </div>
                    </div>
                  </div>

                  {bl?.status === 'listed' && (
                    <div className="space-y-2 mb-4">
                      <div className="text-sm">
                        <span className="text-muted-foreground">Reason:</span>
                        <span className="text-foreground ml-2">{bl?.reason}</span>
                      </div>
                      <div className="text-sm">
                        <span className="text-muted-foreground">Listed Date:</span>
                        <span className="text-foreground ml-2">{bl?.listedDate}</span>
                      </div>
                    </div>
                  )}

                  <Button
                    variant={bl?.status === 'listed' ? 'default' : 'outline'}
                    size="sm"
                    iconName="ExternalLink"
                    onClick={() => window.open(bl?.url, '_blank')}
                  >
                    {bl?.status === 'listed' ? 'Request Delisting' : 'Check Status'}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlacklistMonitor;